import { storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc';
import { colorStyle, optimizeImage, resolveAsset, srcSet } from '@/lib/helpers';

const PADDING_X = 'px-4 sm:px-12 lg:px-16';

// Top and bottom padding are picked separately, so a section can sit tight against
// its neighbour (e.g. a "See more features" toggle right under the pricing cards).
// "default" is the padding every section had before the option existed.
const PADDING_TOP = {
  default: 'pt-8 sm:pt-12 lg:pt-24',
  small: 'pt-4 sm:pt-6 lg:pt-8',
  none: 'pt-0',
};
const PADDING_BOTTOM = {
  default: 'pb-8 sm:pb-12 lg:pb-24',
  small: 'pb-4 sm:pb-6 lg:pb-8',
  none: 'pb-0',
};

const isSvg = (src) => /\.svg$/i.test(src);

// Sizes to offer a Fill image in. A section spans the viewport up to page width
// (1920px), so a phone fetches ~640px and a large screen 1920–2560px (retina).
const FILL_WIDTHS = [640, 960, 1280, 1600, 1920, 2560];

// A Tile is a CSS background repeated at its own size, since a pattern's tile
// size is part of its design (WebP through Storyblok's image service; SVGs as is).
function tileStyle(src, blok) {
  const url = isSvg(src) ? src : optimizeImage(src, 0, 0);
  return {
    backgroundImage: `url("${url.replace(/"/g, '%22')}")`,
    backgroundRepeat: 'repeat',
    backgroundPosition: blok.background_image_position || 'center',
  };
}

/**
 * Full-width page section. An optional Anchor ID lands on the element as `id`,
 * so same-page links can target it and page-level scripts (e.g. the freelancer-
 * tools category filter) can find and show/hide one section at a time.
 *
 * An optional background image sits over the background colour. Fill renders a
 * real <img> behind the content rather than a CSS background, so it gets a
 * responsive srcset (a phone doesn't download the desktop size) and lazy
 * loading; the section becomes a stacking context (`isolate`) so the image's
 * negative z-index puts it above the colour but below the content.
 */
const Section = ({ blok }) => {
  const maxWidth = blok.max_width || 'max-w-page';
  const paddingTop = PADDING_TOP[blok.padding_top] || PADDING_TOP.default;
  const paddingBottom = PADDING_BOTTOM[blok.padding_bottom] || PADDING_BOTTOM.default;

  const bgSrc = resolveAsset(blok.background_image);
  const tile = !!bgSrc && blok.background_image_fit === 'tile';
  const fill = !!bgSrc && !tile;

  const style = { ...colorStyle(blok), ...(tile ? tileStyle(bgSrc, blok) : {}) };
  // Anchor for any absolutely-positioned children inside the section, and for
  // the Fill image.
  if (blok.position_relative || fill) style.position = 'relative';

  return (
    <section
      {...storyblokEditable(blok)}
      id={blok.anchor_id || undefined}
      className={`${maxWidth} mx-auto ${PADDING_X} ${paddingTop} ${paddingBottom} ${fill ? 'isolate' : ''}`.replace(/\s+/g, ' ').trim()}
      style={style}
    >
      {fill && (
        <img
          src={isSvg(bgSrc) ? bgSrc : optimizeImage(bgSrc, 1920, 0)}
          srcSet={isSvg(bgSrc) ? undefined : srcSet(bgSrc, FILL_WIDTHS)}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 -z-10 w-full h-full object-cover pointer-events-none"
          style={{ objectPosition: blok.background_image_position || 'center' }}
        />
      )}
      {blok.body?.map((nestedBlok) => (
        <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
      ))}
    </section>
  );
};

export default Section;
